"use client";

import { useEffect } from "react";
import { onCLS, onFCP, onINP, onLCP, onTTFB } from "web-vitals";

import { WebVitalsCollector, deriveRoute } from "./web-vitals-collector";

import type { FrozenContext, WebVitalReport } from "./web-vitals-collector";

/**
 * Public, per-partner ingest config. The `partnerId` is a public label (it ships
 * in the client bundle) that identifies the partner to the RUM service; there is
 * no secret — the service gates cross-site requests on the browser-enforced
 * `Origin` allowlist, not on this value. Both are read from `NEXT_PUBLIC_*` env —
 * an app without them (every partner except the ones we've onboarded) mounts the
 * collector as a no-op, which is how this stays scoped to `apps/case` in v1.
 */
function readConfig(): { url: string; partnerId: string } | null {
  const url = process.env["NEXT_PUBLIC_WEB_VITALS_INGEST_URL"];
  const partnerId = process.env["NEXT_PUBLIC_HALL_OF_RECORDS_PARTNER_ID"];
  if (!url || !partnerId) return null;
  return { url, partnerId };
}

/**
 * Send a batch as a CORS-safelisted `text/plain` beacon. `text/plain` avoids a
 * preflight `OPTIONS` (which `sendBeacon` can't carry), and `sendBeacon`
 * survives page unload; `fetch(..., { keepalive: true })` is the fallback where
 * `sendBeacon` is unavailable or rejects the payload. Fire-and-forget: the
 * server returns 204 and the collector never inspects the response.
 */
function sendReports(
  url: string,
  partnerId: string,
  reports: WebVitalReport[],
): void {
  const body = JSON.stringify({ partnerId, reports });

  if (
    typeof navigator !== "undefined" &&
    typeof navigator.sendBeacon === "function"
  ) {
    const blob = new Blob([body], { type: "text/plain" });
    if (navigator.sendBeacon(url, blob)) return;
  }

  void fetch(url, {
    method: "POST",
    body,
    keepalive: true,
    headers: { "Content-Type": "text/plain" },
  }).catch(() => {
    // Beacons are best-effort; a failed send is dropped, not retried.
  });
}

/**
 * Mounts the Core Web Vitals beacon collector. Registers the five web-vitals
 * metrics and flushes revisioned, batched beacons to the RUM ingest endpoint at
 * each `visibilitychange → hidden` (interim) and at `pagehide` (final). Renders
 * nothing. A no-op unless the partner's ingest env is configured.
 */
export function WebVitals() {
  useEffect(() => {
    const config = readConfig();
    if (!config) return;

    const context = (): FrozenContext => ({
      // Stamped once, when the collector first sees a metric `id`, then frozen.
      ts: Date.now(),
      route: deriveRoute(window.location.pathname),
      path: window.location.pathname,
    });

    const collector = new WebVitalsCollector(
      (reports) => sendReports(config.url, config.partnerId, reports),
      context,
    );

    const record = (metric: Parameters<typeof collector.record>[0]) =>
      collector.record(metric);
    onCLS(record);
    onFCP(record);
    onINP(record);
    onLCP(record);
    onTTFB(record);

    const flushOnHidden = () => {
      if (document.visibilityState === "hidden") collector.flush();
    };
    const flush = () => collector.flush();

    document.addEventListener("visibilitychange", flushOnHidden);
    window.addEventListener("pagehide", flush);

    return () => {
      document.removeEventListener("visibilitychange", flushOnHidden);
      window.removeEventListener("pagehide", flush);
    };
  }, []);

  return null;
}
